/**
 * Channel utilities that depend on Electron/IPC
 * Works in both main and renderer processes.
 */
import {
  installedVersionWithSuffix,
  isStaticChannel,
  reportingChannelAtStartup,
  StaticBuildChannel,
} from '@highlight/common'
import { GlobalStore, isRenderer } from '@highlight/ipc'
import { isDev } from '../platform'

let startupChannel: StaticBuildChannel | string | undefined

export function isProductionChannel(channel?: StaticBuildChannel | string): boolean {
  if (!channel) {
    channel = getInstalledChannel()
  }

  // These channels are always considered production channels:
  // • Use the production backend API
  // • Use production telemetry (amplitude, sentry)
  // • Use production audio pipeline
  return (
    channel === StaticBuildChannel.latest ||
    channel === StaticBuildChannel.beta ||
    channel === StaticBuildChannel.rc ||
    channel === StaticBuildChannel.trustedTesters
  )
}

export function isTrustedTestersChannel(channel?: StaticBuildChannel | string): boolean {
  if (!channel) {
    channel = getInstalledChannel()
  }
  return channel === StaticBuildChannel.trustedTesters
}

export function isAlphaOrDynamicChannel(channel?: StaticBuildChannel | string): boolean {
  if (!channel) {
    channel = getInstalledChannel()
  }
  return !isProductionChannel(channel) && (channel === StaticBuildChannel.alpha || !isStaticChannel(channel))
}

export function isAlpha(channel?: StaticBuildChannel | string): boolean {
  if (!channel) {
    channel = getInstalledChannel()
  }
  return channel === StaticBuildChannel.alpha
}

export function isAlphaOrBetaChannel(channel: StaticBuildChannel | string = getStartupChannel()): boolean {
  return channel === StaticBuildChannel.alpha || channel === StaticBuildChannel.beta
}

export function getCurrentVersion(): string {
  if (isRenderer) {
    const version = typeof window !== 'undefined' ? window.highlight?.version : undefined
    if (!version) {
      throw new Error('Could not determine installed version')
    }
    return version
  } else {
    // In main process, use Electron's app.getVersion() which reads from the packaged app's package.json
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { app } = require('electron')
    const version = app.getVersion()
    if (!version) {
      throw new Error('Could not determine installed version')
    }
    return version
  }
}

export function getInstalledVersionWithSuffix(): string {
  return installedVersionWithSuffix(getCurrentVersion(), getInstalledChannel())
}

export function getInstalledChannel(): StaticBuildChannel | string {
  if (isDev) {
    return StaticBuildChannel.alpha
  }
  if (isRenderer) {
    const channel = typeof window !== 'undefined' ? window.highlight?.installedChannel : undefined
    if (!channel) {
      console.error('Could not determine installed channel')
      return typeof window !== 'undefined'
        ? (window.highlight?.channel ?? StaticBuildChannel.latest)
        : StaticBuildChannel.latest
    }
    return channel
  } else {
    // In main process, read channel from the app's package.json
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { app } = require('electron')
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const path = require('path')
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const fs = require('fs')

    const packageJsonPath = path.join(app.getAppPath(), 'package.json')
    try {
      const packageJson = JSON.parse(fs.readFileSync(packageJsonPath, 'utf-8'))
      if (!packageJson.channel) {
        throw new Error('Could not determine installed channel')
      }
      return packageJson.channel
    } catch {
      console.error('Could not determine installed channel')
      return StaticBuildChannel.latest
    }
  }
}

export function getCurrentChannel(): StaticBuildChannel | string {
  if (isDev) {
    return StaticBuildChannel.alpha
  }
  return GlobalStore.settings().get<StaticBuildChannel | string>('releaseChannel') ?? getInstalledChannel()
}

export function initializeStartupChannel(): StaticBuildChannel | string {
  startupChannel ??= getCurrentChannel()
  return startupChannel
}

export function getStartupChannel(): StaticBuildChannel | string {
  if (startupChannel === undefined) {
    if (!isRenderer) {
      return getInstalledChannel()
    }
    startupChannel = typeof window !== 'undefined' ? window.highlight?.channel : undefined
    startupChannel ??= getCurrentChannel()
  }
  return startupChannel
}

export function getReportingChannelAtStartup(): StaticBuildChannel | string {
  return reportingChannelAtStartup(getStartupChannel(), getInstalledChannel())
}
